import React, { createContext, useContext, useState, useCallback, useEffect, lazy, Suspense } from "react";
import { LoadingFallback } from "@/components/TopLoadingBar";
const StaffProfileDialog = lazy(() => import("@/components/StaffProfileDialog"));
import PasswordVerificationDialog from "@/components/PasswordVerificationDialog";
import { usePortal } from "@/lib/PortalContext";
import { useSensitiveAccess } from "@/hooks/useSensitiveAccess";
import { api } from "@/api/apiClient";

const StaffProfileContext = createContext();

export function StaffProfileProvider({ children }) {
  const { isManagerView } = usePortal();
  const [profileUserBase, setProfileUserBase] = useState(null);
  const [profileUserFull, setProfileUserFull] = useState(null);
  const [profileOptions, setProfileOptions] = useState({});
  const [profileStaffing, setProfileStaffing] = useState(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const { sensitiveToken, isVerified, grantAccess } = useSensitiveAccess();

  const profileUser = profileUserFull || profileUserBase;

  const openStaffProfile = useCallback((user, staffing = null, options = {}) => {
    if (!isManagerView || !user) return;
    setProfileOptions(options);
    setProfileUserBase(user);
    setProfileUserFull(null);
    setProfileStaffing(staffing);
  }, [isManagerView]);

  useEffect(() => {
    if (!profileUserBase || !sensitiveToken) {
      setProfileUserFull(null);
      return;
    }
    const userId = profileUserBase._id || profileUserBase.id;
    let cancelled = false;
    api.entities.User.getSensitive(userId, sensitiveToken)
      .then(data => { if (!cancelled) setProfileUserFull(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [profileUserBase?._id, profileUserBase?.id, sensitiveToken]);

  const handleClose = (open) => {
    if (!open) {
      setProfileUserBase(null);
      setProfileUserFull(null);
      setProfileStaffing(null);
    }
  };

  const requestVerify = useCallback(() => {
    setShowPasswordDialog(true);
  }, []);

  const handleVerified = useCallback((token, expiresIn) => {
    grantAccess(token, expiresIn);
  }, [grantAccess]);

  return (
    <StaffProfileContext.Provider value={{ openStaffProfile }}>
      {children}
      {profileUserBase && <Suspense fallback={<LoadingFallback />}><StaffProfileDialog
        user={profileUser}
        open={!!profileUserBase}
        onOpenChange={handleClose}
        isVerified={isVerified}
        sensitiveToken={sensitiveToken}
        onRequestVerify={requestVerify}
        staffing={profileStaffing}
        initialTab={profileOptions.initialTab || "profile"}
        onUpdated={changes => {
          const targetId = profileUser._id || profileUser.id;
          const updateCurrent = previous => previous && (previous._id || previous.id) === targetId ? { ...previous, ...changes } : previous;
          setProfileUserBase(updateCurrent);
          setProfileUserFull(updateCurrent);
          profileOptions.onUpdated?.();
        }}
      />
      </Suspense>}
      <PasswordVerificationDialog
        open={showPasswordDialog}
        onOpenChange={setShowPasswordDialog}
        onVerified={handleVerified}
      />
    </StaffProfileContext.Provider>
  );
}

export function useStaffProfile() {
  const ctx = useContext(StaffProfileContext);
  if (!ctx) throw new Error("useStaffProfile must be used within a StaffProfileProvider");
  return ctx;
}
